import React from 'react';
import {
    ConversationMessageKey,
    OutgoingMessageStatus,
} from 'flow/types/TicketEventData';
import Tooltip from 'flow/components/ui-components/Tooltip';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import MenuItem from 'flow/components/ui-components/MenuItem';
import ContextMenu from 'flow/components/ContextMenu';
import { useAppDispatch } from 'flow/hooks/useRedux';
import {
    removeFailedPlaceholderMessageEvent,
    tryResendTicketMessage,
} from 'flow/components/tickets/Input/InputActions';
import { TicketKey } from 'flow/types/EntityKeys';
import { keyIsTypeConversationMessagePlaceholder } from 'flow/utils/ticket-utils';
import useTicketAction from 'flow/hooks/useTicketAction';

interface Props {
    ticketKey: TicketKey;
    messageKey: ConversationMessageKey;
    status: OutgoingMessageStatus;
}

const getStatusI18Key = (status: OutgoingMessageStatus) => {
    switch (status) {
        case 'DELETED':
            return 'message.status.deleted';
        case 'DELIVERED':
            return 'message.status.delivered';
        case 'FAILED':
            return 'message.status.failed';
        case 'READ':
            return 'message.status.read';
        case 'SENDING':
            return 'message.status.sending';
        case 'SENT':
            return 'message.status.sent';
        default:
            return '';
    }
};

const MessageStatus = ({ status, ticketKey, messageKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();

    if (status === 'FAILED') {
        return (
            <div className="flex items-end">
                <Tooltip label={i18next.t('message.status.failed')}>
                    {keyIsTypeConversationMessagePlaceholder(messageKey) ? (
                        <ContextMenu
                            triggerLabel={i18next.t('message.status.failed')}
                            icon={<Icon iconKey="error" size="1.25rem" />}
                            className="text-red bg-transparent"
                        >
                            <MenuItem
                                onClick={() =>
                                    dispatch(
                                        removeFailedPlaceholderMessageEvent(
                                            ticketKey,
                                            messageKey,
                                        ),
                                    )
                                }
                            >
                                {i18next.t('remove')}
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    runTicketAction(
                                        tryResendTicketMessage(
                                            ticketKey,
                                            messageKey,
                                        ),
                                    )
                                }
                            >
                                {i18next.t('chat.try_again')}
                            </MenuItem>
                        </ContextMenu>
                    ) : (
                        <Icon
                            iconKey="error"
                            size="1.25rem"
                            className="text-red"
                        />
                    )}
                </Tooltip>
            </div>
        );
    }

    return (
        <span className="flex items-end text-xs/5 text-gray-300">
            {i18next.t(getStatusI18Key(status))}
        </span>
    );
};

export default MessageStatus;
